(() => {
  if (window.PYKCards) return;
  const config = JSON.parse(document.querySelector('[data-pyk-card-config]')?.textContent || '{}');
  const text = config.strings || {};
  const root = window.Shopify?.routes?.root || '/';
  const translate = (key, fallback) => text[key] || fallback;
  const money = (amount, currency = config.currency || 'INR') => new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style: 'currency', currency, maximumFractionDigits: amount % 100 ? 2 : 0 }).format(amount / 100);
  const element = (tag, className, content) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (content !== undefined) node.textContent = content;
    return node;
  };
  const status = element('div', 'pyk-card-status');
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  document.body.append(status);
  let dialog, context, busy = false, opening = 0, scanScheduled = false;
  const observedLayouts = new WeakSet();
  const alignPrices = () => {
    const layouts = new Set([...document.querySelectorAll('.pyk-card')].map(card => card.closest('product-list, .fc-track, .pd-track, .preview-grid') || card.parentElement));
    layouts.forEach(layout => {
      const cards = [...layout.querySelectorAll('.pyk-card')].filter(card => card.getBoundingClientRect().width > 0);
      cards.forEach(card => card.querySelector('.pyk-card__prices').style.minHeight = '0px');
      const rows = new Map();
      cards.forEach(card => {
        const top = Math.round(card.getBoundingClientRect().top);
        if (!rows.has(top)) rows.set(top, []);
        rows.get(top).push(card.querySelector('.pyk-card__prices'));
      });
      rows.forEach(prices => {
        const height = Math.max(...prices.map(price => price.getBoundingClientRect().height));
        prices.forEach(price => price.style.minHeight = `${height}px`);
      });
      if (!observedLayouts.has(layout) && typeof ResizeObserver !== 'undefined') {
        observedLayouts.add(layout);
        let previousWidth = layout.getBoundingClientRect().width;
        new ResizeObserver(() => {
          const width = layout.getBoundingClientRect().width;
          if (width !== previousWidth) { previousWidth = width; requestAnimationFrame(alignPrices); }
        }).observe(layout);
      }
    });
  };
  const announce = message => { status.textContent = message; };
  const request = async (path, options = {}) => {
    const response = await fetch(root + path, { credentials: 'same-origin', ...options, signal: options.signal || AbortSignal.timeout(15000) });
    const data = await response.json();
    if (!response.ok) {
      const error = new Error(data.description || data.message || translate('retry', 'Couldn’t load. Please try again.'));
      error.status = response.status;
      throw error;
    }
    return data;
  };
  const ensureDialog = () => {
    if (dialog) return dialog;
    dialog = element('dialog', 'pyk-card-dialog');
    dialog.setAttribute('aria-labelledby', 'pyk-card-dialog-title');
    document.body.append(dialog);
    dialog.addEventListener('close', () => {
      document.body.style.overflow = dialog.dataset.previousOverflow || '';
      const origin = context?.trigger;
      if (origin?.isConnected) origin.focus({ preventScroll: true });
    });
    dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
    return dialog;
  };
  const openDialog = () => {
    ensureDialog();
    if (!dialog.open) {
      dialog.dataset.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      dialog.showModal();
    }
  };
  const closeButton = () => {
    const button = element('button', 'pyk-card-dialog__close');
    button.type = 'button';
    button.setAttribute('aria-label', translate('close', 'Close'));
    button.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6"/></svg>';
    button.addEventListener('click', () => dialog.close());
    return button;
  };
  const renderMessage = (message, retry) => {
    ensureDialog().replaceChildren();
    const body = element('div', 'pyk-card-dialog__body');
    const title = element('h2', '', context?.product?.title || context?.card.querySelector('.pyk-card__title')?.textContent.trim());
    title.id = 'pyk-card-dialog-title';
    const label = element('p', 'pyk-card-dialog__message', message);
    label.setAttribute('role', 'alert');
    body.append(closeButton(), title, label);
    if (retry) {
      const button = element('button', 'pyk-card-dialog__add', translate('try_again', 'Try again'));
      button.type = 'button';
      button.addEventListener('click', retry);
      body.append(button);
    }
    const link = element('a', 'pyk-card-dialog__details', translate('product_details', 'View full product details'));
    link.href = context.card.querySelector('.pyk-card__image-link').href;
    body.append(link);
    dialog.append(body);
    openDialog();
  };
  const variantFor = () => context.product.variants.find(variant => variant.options.every((value, index) => value === context.selected[index]));
  const refreshOptions = () => {
    const { product, selected, add, price, options } = context;
    const variant = variantFor();
    options.querySelectorAll('[data-option-index]').forEach(button => {
      const index = Number(button.dataset.optionIndex);
      const value = button.dataset.value;
      const available = product.variants.some(v => v.available && v.options[index] === value && selected.every((selection, i) => i === index || !selection || v.options[i] === selection));
      button.disabled = !available;
      button.setAttribute('aria-checked', String(selected[index] === value));
      button.setAttribute('aria-label', value + (available ? '' : ' · ' + translate('sold_out', 'Sold out')));
    });
    options.querySelectorAll('[role="radiogroup"]').forEach(group => {
      const buttons = Array.from(group.querySelectorAll('button'));
      const active = buttons.find(b => b.getAttribute('aria-checked') === 'true') || buttons.find(b => !b.disabled);
      buttons.forEach(b => b.tabIndex = b === active ? 0 : -1);
    });
    add.disabled = !variant?.available || busy || context.uncertain;
    add.textContent = variant?.available ? `${translate('add_to_cart', 'Add to cart')} · ${variant.public_title || variant.title}` : translate('select_options', 'Select your options');
    price.textContent = money(variant?.price ?? context.card.dataset.price * 1);
    const variantImage = variant?.featured_image?.src;
    if (variantImage) dialog.querySelector('.pyk-card-dialog__image').src = variantImage;
    const emiHost = context.emi;
    const oldPrice = emiHost.dataset.price;
    const newPrice = variant?.available ? String(variant.price) : '';
    if (oldPrice !== newPrice) {
      emiHost.replaceChildren(element('span', 'pyk-card__emi-fallback', translate('plans_unavailable', 'Payment plans unavailable')));
      emiHost.dataset.price = newPrice;
      if (newPrice) initialiseEMI(emiHost, variant.price, context.card.dataset.currency);
    }
  };
  const renderSelector = () => {
    const { card, product } = context;
    ensureDialog().replaceChildren();
    const body = element('div', 'pyk-card-dialog__body');
    const image = element('img', 'pyk-card-dialog__image');
    image.alt = ''; image.width = 92; image.height = 92;
    image.src = product.featured_image || card.querySelector('img')?.src || '';
    if (!image.getAttribute('src')) image.hidden = true;
    image.addEventListener('error', () => { image.hidden = true; }, { once: true });
    const brand = element('p', 'pyk-card-dialog__brand', card.querySelector('.pyk-card__brand')?.textContent);
    const title = element('h2', '', product.title); title.id = 'pyk-card-dialog-title';
    const price = element('p', 'pyk-card-dialog__price', money(Number(card.dataset.price)));
    const options = element('div', 'pyk-card-dialog__options');
    product.options.forEach((option, index) => {
      const name = typeof option === 'string' ? option : option.name;
      const values = [...new Set(product.variants.map(variant => variant.options[index]))];
      const field = element('fieldset');
      const legend = element('legend', '', name);
      const group = element('div', 'pyk-card-dialog__values');
      group.setAttribute('role', 'radiogroup'); group.setAttribute('aria-label', name);
      values.forEach(value => {
        const button = element('button', 'pyk-card-dialog__option', value);
        button.type = 'button'; button.setAttribute('role', 'radio');
        button.dataset.optionIndex = index; button.dataset.value = value;
        button.addEventListener('click', () => { context.selected[index] = value; context.error.hidden = true; refreshOptions(); });
        button.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const enabled = Array.from(group.querySelectorAll('button:not(:disabled)'));
          const step = ['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1;
          const target = event.key === 'Home' ? enabled[0] : event.key === 'End' ? enabled.at(-1) : enabled[(enabled.indexOf(button) + step + enabled.length) % enabled.length];
          target?.click(); target?.focus();
        });
        group.append(button);
      });
      field.append(legend, group); options.append(field);
    });
    const error = element('p', 'pyk-card-dialog__message'); error.hidden = true; error.setAttribute('role', 'alert');
    const add = element('button', 'pyk-card-dialog__add'); add.type = 'button'; add.addEventListener('click', () => addVariant(variantFor()));
    const emi = element('div', 'pyk-card__emi');
    const details = element('a', 'pyk-card-dialog__details', translate('product_details', 'View full product details')); details.href = card.querySelector('.pyk-card__image-link').href;
    Object.assign(context, { price, options, error, add, emi });
    body.append(closeButton(), image, brand, title, price, options, error, add, emi, details);
    dialog.append(body); refreshOptions(); openDialog();
  };
  const addVariant = async variant => {
    if (busy || !variant?.available) return;
    const current = context;
    busy = true;
    current.trigger.setAttribute('aria-busy', 'true');
    if (current.add) { current.add.disabled = true; current.add.textContent = translate('adding', 'Adding…'); current.add.setAttribute('aria-busy', 'true'); }
    current.options?.querySelectorAll('button').forEach(button => button.disabled = true);
    current.emi?.querySelectorAll('button').forEach(button => button.disabled = true);
    let committed = false;
    try {
      const sections = ['variant-added'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
      const result = await request('cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }], sections: [...new Set(sections)].join(','), sections_url: root + 'variants/' + variant.id }) });
      const items = result.items || [result];
      if (!items.some(item => Number(item.variant_id || item.id) === Number(variant.id))) throw new Error(translate('add_failed', 'Couldn’t confirm the cart. Check your cart before trying again.'));
      committed = true;
      current.trigger.dataset.state = 'success';
      const variantLabel = variant.public_title || variant.title;
      const message = [current.product.title, variantLabel === 'Default Title' ? null : variantLabel, translate('added', 'Added to your cart')].filter(Boolean).join(' · ');
      announce(message);
      if (dialog?.open) dialog.close();
      let cart;
      try { cart = await request('cart.js'); } catch { document.dispatchEvent(new CustomEvent('cart:refresh')); }
      if (cart) {
        cart.sections = result.sections;
        current.card.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items, cart } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
      }
      setTimeout(() => { if (current.trigger.isConnected) delete current.trigger.dataset.state; }, 2200);
    } catch (error) {
      if (!committed) {
        const uncertain = !error.status;
        current.uncertain = uncertain;
        const message = uncertain ? translate('add_failed', 'Couldn’t confirm the cart. Check your cart before trying again.') : error.message;
        if (current.error) { current.error.textContent = message; current.error.hidden = false; }
        else renderMessage(message, uncertain ? null : () => addVariant(variant));
        if (uncertain) {
          const cartLink = element('a', 'pyk-card-dialog__details', translate('check_cart', 'Check your cart'));
          cartLink.href = root + 'cart';
          dialog?.querySelector('.pyk-card-dialog__body')?.append(cartLink);
        }
        announce(message);
      }
    } finally {
      busy = false; current.trigger.removeAttribute('aria-busy');
      if (current.add) { current.add.removeAttribute('aria-busy'); if (!committed) refreshOptions(); }
      current.emi?.querySelectorAll('button').forEach(button => button.disabled = false);
    }
  };
  const activate = async trigger => {
    if (busy) { announce(translate('adding', 'Adding…')); return; }
    const card = trigger.closest('.pyk-card');
    if (!card || card.dataset.available !== 'true' || trigger.getAttribute('aria-busy') === 'true') return;
    const token = ++opening;
    context = { card, trigger, selected: [] };
    trigger.setAttribute('aria-busy', 'true');
    try {
      const product = await request('products/' + encodeURIComponent(card.dataset.productHandle) + '.js');
      if (token !== opening) return;
      context.product = product;
      if (!Array.isArray(product.variants) || !product.variants.some(v => v.available)) { renderMessage(translate('sold_out', 'Sold out')); return; }
      if (product.requires_selling_plan || card.dataset.sellingPlan === 'true') { renderMessage(translate('subscription_details', 'Choose your purchase plan on the product page.')); return; }
      if (product.variants.length === 1 && product.variants[0].title === 'Default Title') await addVariant(product.variants[0]);
      else {
        const initialOption = card.querySelector('.pyk-card__variants');
        const index = Number(card.dataset.selectedOptionIndex ?? initialOption?.dataset.initialOptionIndex);
        const value = card.dataset.selectedOptionValue ?? initialOption?.dataset.initialOptionValue;
        if (Number.isInteger(index) && value && product.variants.some(v => v.available && v.options[index] === value)) context.selected[index] = value;
        renderSelector();
      }
    } catch (error) {
      if (token === opening) renderMessage(translate('load_failed', 'Couldn’t load options. Please try again.'), () => activate(trigger));
    } finally { trigger.removeAttribute('aria-busy'); }
  };
  const mountProviderWidget = (host, source) => {
    const quote = source.querySelector('.dp-collection-price');
    if (!quote || !Number(quote.textContent.replace(/[^\d.]/g, ''))) return;
    // Keep Snapmint's live merchant markup and CSS; only add keyboard/modal behavior.
    const row = source.querySelector('.snap_dp_list');
    const widget = row?.querySelector('.snap_collection_category');
    if (!widget) return;
    widget.setAttribute('role', 'button');
    widget.setAttribute('tabindex', '0');
    widget.setAttribute('aria-haspopup', 'dialog');
    widget.setAttribute('aria-label', `${widget.textContent.trim().replace(/\s+/g, ' ')} · Snapmint`);
    const openPlans = () => {
      if (busy || typeof window.startPop !== 'function') return;
      const suspended = dialog?.open;
      if (suspended) dialog.close();
      try {
        window.startPop(widget);
        const popup = document.getElementById('sm-pop-up-model');
        if (!popup) { if (suspended) openDialog(); return; }
        popup.setAttribute('role', 'dialog'); popup.setAttribute('aria-modal', 'true'); popup.setAttribute('aria-label', translate('view_plans', 'Snapmint · View plans'));
        const close = popup.querySelector('#snapModalCloseon_page');
        if (close) { close.setAttribute('role', 'button'); close.setAttribute('tabindex', '0'); close.setAttribute('aria-label', translate('close', 'Close')); }
        popup.tabIndex = -1;
        const background = [...document.body.children].filter(node => node !== popup && !node.inert);
        background.forEach(node => node.inert = true);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const keyboard = event => {
          if (event.key === 'Escape') { event.preventDefault(); close?.click(); }
          if (event.target === close && ['Enter', ' '].includes(event.key)) { event.preventDefault(); close.click(); }
          if (event.key !== 'Tab') return;
          const controls = [...popup.querySelectorAll('button, a[href], input, select, [tabindex="0"]')].filter(node => !node.disabled && node.getClientRects().length);
          const first = controls[0], last = controls.at(-1);
          if (!first) { event.preventDefault(); popup.focus(); }
          else if (event.shiftKey && (document.activeElement === first || document.activeElement === popup)) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        };
        popup.addEventListener('keydown', keyboard);
        (close || popup).focus();
        const restore = () => { observer.disconnect(); popup.removeEventListener('keydown', keyboard); background.forEach(node => node.inert = false); document.body.style.overflow = previousOverflow; if (suspended && context?.card.isConnected) { openDialog(); widget.focus(); } else if (widget.isConnected) widget.focus(); };
        const observer = new MutationObserver(() => { if (!popup.isConnected || popup.style.display === 'none' || popup.hidden) restore(); });
        observer.observe(document.body, { childList: true }); observer.observe(popup, { attributes: true, attributeFilter: ['style', 'hidden'] });
      } catch { if (suspended) openDialog(); }
    };
    // Capture prevents the provider inline onclick from opening a second popup.
    widget.addEventListener('click', event => { event.preventDefault(); event.stopImmediatePropagation(); openPlans(); }, true);
    widget.addEventListener('keydown', event => {
      if (['Enter', ' '].includes(event.key)) { event.preventDefault(); widget.click(); }
    });
    host.querySelector('.pyk-card__emi-fallback')?.remove();
    host.append(row);
  };
  const initialiseEMI = (host, amount, currency) => {
    if (currency !== 'INR' || !Number.isInteger(amount) || amount <= 0 || typeof window.collectionList !== 'function') return;
    if (!host.querySelector('.pyk-emi-source')) {
      const source = element('div', 'pyk-emi-source'); source.hidden = true;
      const prices = element('div', 'price-list'); const price = element('sale-price', '', String(amount / 100));
      prices.append(price); source.append(prices); host.append(source);
    }
    const source = host.querySelector('.pyk-emi-source');
    if (!source) return;
    source.classList.add('pyk-emi-pending');
    try { window.collectionList('.pyk-emi-pending'); } catch { return; }
    source.classList.remove('pyk-emi-pending');
    if (source.querySelector('.snap_dp_list')) mountProviderWidget(host, source);
  };
  const selectCardVariant = swatch => {
    const card = swatch.closest('.pyk-card');
    if (!card || busy) return;
    const price = Number(swatch.dataset.price);
    const compare = Number(swatch.dataset.comparePrice);
    card.dataset.selectedOptionIndex = swatch.dataset.optionIndex;
    card.dataset.selectedOptionValue = swatch.dataset.optionValue;
    card.dataset.price = String(price);
    card.dataset.available = swatch.dataset.available;
    card.querySelectorAll('[data-pyk-variant]').forEach(option => option.setAttribute('aria-current', String(option === swatch)));
    card.querySelectorAll('.pyk-card__image-link, .pyk-card__title a, .pyk-card__variants-more').forEach(link => { link.href = swatch.href; });
    const image = card.querySelector('.pyk-card__image');
    if (image && swatch.dataset.image) {
      image.removeAttribute('srcset');
      image.src = swatch.dataset.image;
      image.hidden = false;
      card.querySelector('.pyk-card__image-fallback').hidden = true;
    }
    const prices = card.querySelector('.pyk-card__prices');
    prices.querySelector('sale-price').textContent = money(price, card.dataset.currency);
    let original = prices.querySelector('s');
    if (compare > price) {
      if (!original) { original = element('s'); prices.append(original); }
      original.hidden = false;
      original.textContent = money(compare, card.dataset.currency);
    } else if (original) original.hidden = true;
    const cartButton = card.querySelector('[data-pyk-quick-add]');
    if (cartButton) { cartButton.disabled = swatch.dataset.available !== 'true'; delete cartButton.dataset.state; }
    const badge = card.querySelector('.pyk-card__badge');
    if (swatch.dataset.available !== 'true' && !badge) card.querySelector('.pyk-card__media').append(element('span', 'pyk-card__badge', translate('sold_out', 'Sold out')));
    else if (swatch.dataset.available === 'true') badge?.remove();
    const host = card.querySelector('[data-pyk-emi]');
    if (host) {
      const source = card.querySelector('.pyk-emi-source');
      source?.remove();
      host.replaceChildren(element('span', 'pyk-card__emi-fallback', translate('plans_unavailable', 'Payment plans unavailable')));
      if (swatch.dataset.available === 'true') initialiseEMI(host, price, card.dataset.currency);
    }
    alignPrices();
    announce(swatch.getAttribute('aria-label'));
  };
  const scan = () => {
    document.querySelectorAll('.pyk-card').forEach(card => {
      const image = card.querySelector('.pyk-card__image');
      if (image && !image.dataset.pykObserved) {
        image.dataset.pykObserved = 'true';
        const fail = () => { image.hidden = true; card.querySelector('.pyk-card__image-fallback').hidden = false; };
        image.addEventListener('error', fail, { once: true });
        if (image.complete && !image.naturalWidth) fail();
      }
      const host = card.querySelector('[data-pyk-emi]');
      if (host && !host.querySelector('.snap_dp_list') && card.dataset.available === 'true') initialiseEMI(host, Number(card.dataset.price), card.dataset.currency);
    });
    alignPrices();
  };
  document.addEventListener('click', event => {
    const swatch = event.target.closest('[data-pyk-variant]');
    if (swatch) { event.preventDefault(); selectCardVariant(swatch); return; }
    const trigger = event.target.closest('[data-pyk-quick-add]');
    if (trigger) { event.preventDefault(); activate(trigger); }
  });
  document.addEventListener('keydown', event => {
    const swatch = event.target.closest('[data-pyk-variant]');
    if (!swatch) return;
    if (event.key === ' ') { event.preventDefault(); swatch.click(); }
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const options = [...swatch.parentElement.querySelectorAll('[data-pyk-variant]')].filter(option => option.getClientRects().length);
    const step = event.key === 'ArrowLeft' ? -1 : 1;
    const next = event.key === 'Home' ? options[0] : event.key === 'End' ? options.at(-1) : options[(options.indexOf(swatch) + step + options.length) % options.length];
    next?.focus(); next?.click();
  });
  new MutationObserver(records => {
    if (scanScheduled || !records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('.pyk-card') || node.querySelector?.('.pyk-card'))))) return;
    scanScheduled = true; requestAnimationFrame(() => { scanScheduled = false; scan(); });
  }).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', scan);
  const script = document.querySelector('script[src*="checkout-merchant.snapmint.com/js/"]');
  script?.addEventListener('load', scan, { once: true });
  let attempts = 0;
  const waitForProvider = () => {
    scan();
    if (typeof window.collectionList !== 'function' && ++attempts < 40) setTimeout(waitForProvider, 250);
  };
  window.PYKCards = { refresh: scan };
  document.fonts?.ready.then(alignPrices);
  waitForProvider();
})();
